<template>
  <ProSpaceGeoLayout class="documents-grid">
    <template #top>
      <ProSpaceTopPanel :header="getTitle()">
        <template #right>
          <ProSpaceButton
            style="padding-top: 4px; padding-bottom: 4px;"
            icon="chevron-down"
            type="secondary"
            :label="currentPeriod?.name"
            @click="actionPeriod"
          />
        </template>
      </ProSpaceTopPanel>
    </template>
    <template #center>
      <ProSpaceGeoLayout>
        <template #top>
          <ProSpaceFilterPanel
            :searchPlaceholder="$t('searchTxt')"
            :modelFastFilter="modelFastFilter"
            :fastFilters="fastFilters"
            :unicKeyStorage="documentService.constructor.name + 'grid'"
            :txtResetFilters="$t('txtResetFilters')"
            no-mounted-request
            @apply="submitFilter"
            ref="filter-panel"
            :filter-model="['orderAgencyValue', 'type']"
            :excludedFilters="['orderMarsPeriodId', 'orderAgencyId']"
            :oneValueFilters="['orderMarsPeriodId', 'coverage', 'orderAgencyId']"
            @update:modelFastFilter="updateModelFastFilter($event)"
          >
            <template #hint>
              {{ checkAccess('orderAgencyId-ProSpaceFilterSearchfield') ? $t("filterBy") : '' }}
            </template>
            <template #filters="{ filter }">
              <ProSpaceFilterSearchfield
                v-if="checkAccess('orderAgencyId-ProSpaceFilterSearchfield')"
                v-model="filter.orderAgencyId"
                :closeTxt="$t('closeTxt')"
                :placeholderValue="$t('agency')"
                :applyTxt="$t('applyTxt')"
                :clearFilterTxt="$t('clearTxt')"
                :cancelTxt="$t('cancelTxt')"
                :txtTitleModal="$t('agencyList')"
                :txtNameColumn="$t('network')"
                :service="agencyService"
                get-method="grid"
                optionLabel="value"
                optionValue="id"
                optionCode="code"
                @apply="(model) => customFilterApply(filter, model)"
                :default-filter="{
                  disabled: false,
                }"
              />
              <ProSpaceFilterDateTimeRange
                v-if="checkAccess('orderAgencyId-ProSpaceFilterSearchfield')"
                v-model="filter.createdAt"
                :placeholder-value="$t('documentCreatedAt')"
                :closeTxt="$t('closeTxt')"
                :applyTxt="$t('applyTxt')"
                :clearFilterTxt="$t('clearTxt')"
                :fromTxt="$t('fromTxt')"
                :toTxt="$t('toTxt')"
                @apply="(model) => customFilterApply(filter, model)"
              />
              <ProSpaceCustomFilterPanel
                v-if="checkAccess('orderAgencyId-ProSpaceFilterSearchfield')"
                :customTxt="$t('custom')"
                :customFilterTxt="$t('customFilterTxt')"
                :selectAllTxt="$t('selectAllTxt')"
                :closeTxt="$t('closeTxt')"
                :applyTxt="$t('applyTxt')"
                :clearFilterTxt="$t('clearTxt')"
                :fromTxt="$t('fromTxt')"
                :toTxt="$t('toTxt')"
                :services="customServices"
                :unicKeyStorage="documentService.constructor.name + 'grid'"
                @apply="(model) => customFilterApply(filter, model)"
              />
            </template>
          </ProSpaceFilterPanel>
        </template>
        <template #center>
          <ProSpaceMainGrid
            ref="grid"
            :service="documentService"
            @selected-change="selectedHandler"
            no-mounted-request
            getMethod="grid"
            selectionMode="single"
            :autoWidthStatusId="autoWidthStatusId"
          >
            <template #columns>
              <Column
                field="type"
                :header="this.$t('documentType')"
                sortable
                headerClass="move-sort-head"
              >
                <template #body="field">
                  <ProSpaceTableHeaderCell
                    v-if="field.data && !getGridLoading"
                    isTitleInline
                    :status="getStatus(field.data.type)"
                    :title="field.data.type === 'plan' ? $t('documentPlanTab') : $t('documentFactTab')"
                    :calcWidthStatusId="!autoWidthStatusId"
                  />
                </template>
              </Column>
              <Column
                v-if="checkAccess('orderAgencyValue-Column')"
                field="orderAgencyValue"
                :header="$t('documentOrderAgencyValue')"
                sortable
              >
                <template #body="field">
                  <ProSpaceDefaultTextColumn v-if="field.data && !getGridLoading">
                    {{ field.data.orderAgencyValue }}
                  </ProSpaceDefaultTextColumn>
                </template>
              </Column>
              <Column
                field="format"
                :header="$t('documentFormat')"
                sortable
              >
                <template #body="field">
                  <ProSpaceDefaultTextColumn v-if="field.data && !getGridLoading">
                    {{ field.data.format }}
                  </ProSpaceDefaultTextColumn>
                </template>
              </Column>
              <Column field="size" :header="$t('documentSize')" sortable>
                <template #body="field">
                  <ProSpaceDefaultTextColumn v-if="field.data && !getGridLoading">
                    {{ byteToKBString(field.data.size) }}
                  </ProSpaceDefaultTextColumn>
                </template>
              </Column>
              <Column
                field="createdAt"
                sortable
                :header="$t('documentCreatedAt')"
                style="min-width: 200px;"
              >
                <template #body="field">
                  <ProSpaceDateTimeColumn
                    v-if="field.data && !getGridLoading"
                    :date="field.data.createdAt"
                    :dateFormat="userinfo.dateFormat"
                    :timeFormat="userinfo.timeFormat"
                  />
                </template>
              </Column>
              <Column field="actions" class="column-end column-end-30">
                <template #body="field">
                  <ProSpaceIconButton
                    v-if="field.data && !getGridLoading"
                    icon="file-download"
                    iconWidth="11px"
                    iconHeight="15px"
                    coloredIcon
                    @click="downloadAttachment(field.data)"
                  />
                </template>
              </Column>
            </template>
            <template #empty>
              <ProSpaceEmptyGrid
                icon="error-message"
                :title="$t('noDataTitle')"
                :text="$t('noDataTxt')"
              />
            </template>
          </ProSpaceMainGrid>
        </template>
      </ProSpaceGeoLayout>
      <ProSpaceHistoryModal
        ref="history"
        v-model="showHistoryModal"
        modelName="DocumentApiModel"
        @close="closeHistory"
      />
    </template>
    <template #right>
      <DocumentPanel
        v-if="selectedModel.length > 0"
        ref="right-panel"
        :service="documentService"
        :ids="selectedModel"
        :options="dotsOptions"
        @clear="clear"
        @edit="editItem"
        @delete="deleteItem"
        @history="showHistory"
        @downloadAttachment="downloadAttachment"
      />
    </template>
  </ProSpaceGeoLayout>
</template>
<script>
import {
  ProSpaceFilterPanel,
  ProSpaceCustomFilterPanel,
  ProSpaceMultiSelect,
  ProSpaceMainGrid,
  ProSpaceDots,
  ProSpaceModal,
  ProSpaceGeoLayout,
  ProSpaceTopPanel,
  ProSpaceIconButton,
  ProSpaceButton,
  ProSpaceTableHeaderCell,
  ProSpaceDateColumn,
  ProSpaceDefaultTextColumn,
  ProSpaceEmptyGrid,
  ProSpaceLeftPanel,
  ProSpaceTree,
  ProSpaceVLayout,
  ProSpaceHLayout,
  ProSpaceFilterContainsChips,
  ProSpaceFilterDatesRange,
  ProSpaceDropdown,
  ProSpaceInputCalendar,
  ProSpaceInputNumber,
  ProSpaceInputSwitch,
  ProSpaceInputText,
  ProSpaceIconsGrid,
  ProSpaceFilterDateTimeRange,
  ProSpaceDateTimeColumn,
  ProSpaceTag,
  ProSpaceFilterSearchfield
} from "@prospace/prospace-components-library";
import { DocumentService } from "../../services/DocumentService.js";
import { OrderService } from "../../services/OrderService.js";
import { AgencyService } from "../../services/AgencyService.js";
import { MarsPeriodService } from "../../services/MarsPeriodService.js";
import { FileService } from "../../../../../Frontends/ProSpace/services/FileService";
import Column from "primevue/column";
import DocumentPanel from "./components/DocumentPanel";
import ProSpaceHistoryModal from "../../../../../Frontends/ProSpace/components/modals/ProSpaceHistoryModal.vue";
const _ = require("lodash");
import { mapGetters } from "vuex";
import accessMixin from "../../../../../Frontends/Common/utils/elementAccess/accessMixin";
export default {
  mixins: [accessMixin],
  components: {
    ProSpaceFilterPanel,
    ProSpaceCustomFilterPanel,
    ProSpaceMultiSelect,
    ProSpaceMainGrid,
    ProSpaceDots,
    ProSpaceModal,
    Column,
    ProSpaceGeoLayout,
    ProSpaceTopPanel,
    ProSpaceIconButton,
    ProSpaceButton,
    ProSpaceTableHeaderCell,
    ProSpaceDateColumn,
    ProSpaceDefaultTextColumn,
    ProSpaceEmptyGrid,
    DocumentPanel,
    ProSpaceHistoryModal,
    ProSpaceLeftPanel,
    ProSpaceTree,
    ProSpaceVLayout,
    ProSpaceHLayout,
    ProSpaceFilterContainsChips,
    ProSpaceFilterDatesRange,
    ProSpaceDropdown,
    ProSpaceInputCalendar,
    ProSpaceInputNumber,
    ProSpaceInputSwitch,
    ProSpaceInputText,
    ProSpaceIconsGrid,
    ProSpaceFilterDateTimeRange,
    ProSpaceDateTimeColumn,
    ProSpaceTag,
    ProSpaceFilterSearchfield
  },
  data() {
    return {
      showModal: false,
      showHistoryModal: false,
      // Grid Panel
      selectedModel: [],
      spHide: false,

      // Service
      documentService: new DocumentService(),
      orderService: new OrderService(),
      agencyService: new AgencyService(),
      marsPeriodService: new MarsPeriodService(),
      fileService: new FileService(),
      //InlinePanel
      masterId: null,
      isEdit: false,
      isCreate: false,
      modalModel: {},
      type: "",
      autoWidthStatusId: false,

      // Multi filter
      /*modelFastFilter: {
        label: this.$t("allTxt"),
        value: false,
        field: "disabled",
      },*/
    };
  },
  computed: {
    ...mapGetters({
      userinfo: "userinfoStore/getUserinfo",
      access: "securityRoleAccess/access",
      currentPeriod: "marsPeriodStore/getMarsPeriod"
    }),
    modelFastFilter: {
      get() {
        if(this.type) {
          return this.fastFilters.find(f => f.value.find(s => s.value === this.type));
        } else {
          return this.fastFilters[0];
        }
      },
      set(val) {
        Object.assign(this.modelFastFilter, val);
      }
    },
    customServices() {
      return [
        {
          service: new DocumentService(),
          getMethod: "getTypes",
          optionValue: "value",
          optionLabel: "label",
          type: "checkboxes",
          model: "type",
          needSelectAll: true,
          label: this.$t("documentType"),
          selected: {},
        },
        {
          type: "range",
          model: "size",
          label: `${this.$t("documentSize")}, КБ`,
          optionValue: "value",
          selected: {},
        },
      ];
    },
    dotsOptions() {
      const optionsArr = [
        {
          name: "showHistory",
          text: this.$t("history"),
          icon: "history",
          method: this.showHistory,
          liteRef: "history-option",
          iconSize: "20px",
          iconGap: 7,
        }
      ];
      return this.wrapperCheckRoles(optionsArr);
    },
    fastFilters() {
      let filtersArray = [
        {
          label: this.$t("allTxt"),
          value: [
            { field: "disabled", value: false },
            { field: "orderMarsPeriodId", value: this.currentPeriod?.id },
          ]
        },
        {
          label: this.$t("documentPlanTab"),
          value: [
            { field: "type", value: "plan" },
            { field: "orderMarsPeriodId", value: this.currentPeriod?.id },
            { field: "disabled", value: false },
          ],
        },
        {
          label: this.$t("documentFactTab"),
          value: [
            { field: "type", value: "fact" },
            { field: "orderMarsPeriodId", value: this.currentPeriod?.id },
            { field: "disabled", value: false },
          ],
        },
      ]

      if (this.isUserHasAgency) {
        filtersArray = filtersArray.map(filter => {
          filter.value.push({
            field: "orderAgencyId",
            value: this.userinfo?.agencyLinkAgencyId
          })
          return filter
        })
      }

      return filtersArray;
    },
    isUserHasAgency() {
      return this.userinfo.agencyLinkType == "AGENCY"
    },
    getSingleLoaderConfig() {
      return {
        cols: [100],
        rows: [
          { width: 100, height: 30, borderRadius: '5px' },
        ]
      }
    },
    getGridLoading() {
      return this.$refs.grid.loading;
    }
  },
  watch: {
    currentPeriod: {
      handler: function () {
        this.$nextTick(() => {
          if (Object.keys(this.$refs["filter-panel"]?.filter).length) {
            this.type = this.$refs["filter-panel"]?.filter?.type?.and[0];
          }
          this.$refs["filter-panel"].submitFilter(true, false);
        });
        this.clear();
      },
      immediate: true
    }
  },
  methods: {
    byteToKBString(val) {
      if (val === null || val === undefined)
        return "";

      return (val / 1024).toLocaleString(undefined);
    },
    updateModelFastFilter(val) {
      this.type = val.value.find(f => f.field === 'type')?.value;
      this.modelFastFilter = val;
      this.clear()
    },
    getTitle() {
      let me = this;
      if (me.isUserHasAgency) {
        return me.userinfo.agencyLinkAgencyValue + me.$t('documentsTitle');
      } else {
        return me.$t('mars') + me.$t('documentsTitle');
      }
    },
    actionPeriod() {
      this.$store.commit("marsPeriodStore/SET_PERIODMODALSTATE", true);
    },
    closeHistory() {
      this.showHistoryModal = false;
    },
    showHistory(record) {
      this.$refs.history.tabs[0].masterId = record.id;
      this.$refs.history.masterName = record.code;
      this.$nextTick(() => {
        this.showHistoryModal = true;
      });
    },
    submitFilter({ filter, search }) {
      if (filter && filter.size && Array.isArray(filter.size.and)) {
        let f = filter.size.and[0];
        if (f.ge) f.ge *= 1024;
        if (f.le) f.le *= 1024;
      }
      this.$refs.grid.submitFilter(filter, search);
    },
    emitFilter() {
      this.$refs["filter-panel"].submitFilter();
    },
    customFilterApply(filter, model) {
      filter = Object.assign(filter, model);
      this.$refs["filter-panel"].submitFilter();
    },
    actionUpdate() {
      this.$refs.grid.getRecords(true);
      if (this.$refs["right-panel"]) {
        this.$refs["right-panel"].update();
      }
      this.$refs.grid.resetSkip();
    },
    loadingHandler(val) {
      this.$refs.grid.loading = val;
    },
    selectedHandler({ data }) {
      const id = data.id
      this.selectedModel = [id];
      this.masterId = id;
    },
    showChangeModal() {
      this.isEdit = false;
      this.isCreate = true;
      this.modalModel = {};
      this.showModal = true;
    },
    clear() {
      if(this.$refs.grid) {
        this.selectedModel = {};
        this.$refs.grid.selectedModel = [];
      }
    },
    editItem(model) {
      if (!model) return;
      this.isEdit = true;
      this.isCreate = false;
      this.modalModel = _.cloneDeep(model);
      this.showModal = true;
    },
    changeItem() {
      let method;
      if (this.isCreate) {
        method = "create";
      }
      if (this.isEdit) {
        method = "update";
      }
      this.documentService[method](this.modalModel).then(() => {
        this.actionUpdate();
      });
    },
    deleteItem(model) {
      if (!model) return;
      this.documentService.delete(model).then(() => {
        this.actionUpdate();
        this.selectedModel = {};
      });
    },
    getStatus(status) {
      return {
        text: this.$t(status),
        type: status === 'plan' ? 'info' : 'teal',
      };
    },
    handleDots(action, item) {
      var option = this.dotsOptions.find((o) => o.name === action);
      option.method(item);
    },
    downloadAttachment(record) {
      this.fileService.getFileToken(record.fileGFID).then((response) => {
        if (response != null) {
          this.fileService
            .downloadFile(response.data.token)
            .then((fileResponse) => {
              let link = document.createElement("a");
              link.href = window.URL.createObjectURL(fileResponse.data);
              let name = fileResponse.headers["content-disposition"]
                .split(";")
                .find((n) => n.includes("filename="))
                .replace("filename=", "")
                .trim();
              link.download = decodeURI(name).replace("%2b", "+");
              link.click();
            });
        }
      });
    },
  },
};
</script>
<style lang="scss">
.documents-grid {
  .main-grid {
    .p-datatable .p-datatable-thead .p-column-resizer {
      transform: scale(1, 1);
    }
  }
}
</style>
